import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, vModelText as _vModelText, withDirectives as _withDirectives, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, createBlock as _createBlock, resolveComponent as _resolveComponent } from "vue"

const _hoisted_1 = { class: "home-container bsp-page" }
const _hoisted_2 = {
  key: 0,
  class: "home bsp-page__content"
}
const _hoisted_3 = {
  key: 0,
  class: "metrics-page"
}
const _hoisted_4 = {
  class: "bsp-section metrics-section",
  "aria-labelledby": "metrics-title"
}
const _hoisted_5 = { class: "bsp-filter-grid home-metrics-filters" }
const _hoisted_6 = { class: "bsp-native-field" }
const _hoisted_7 = { class: "bsp-native-field" }
const _hoisted_8 = { class: "bsp-filter-actions home-metrics-actions" }
const _hoisted_9 = {
  key: 0,
  class: "bsp-inline-loader"
}
const _hoisted_10 = {
  key: 1,
  class: "bsp-table-shell"
}
const _hoisted_11 = { key: 0 }
const _hoisted_12 = { key: 1 }
const _hoisted_13 = { key: 2 }
const _hoisted_14 = { key: 3 }
const _hoisted_15 = { key: 0 }
const _hoisted_16 = { key: 1 }
const _hoisted_17 = { key: 2 }
const _hoisted_18 = { key: 3 }
const _hoisted_19 = { key: 0 }
const _hoisted_20 = { key: 1 }
const _hoisted_21 = { key: 2 }
const _hoisted_22 = { key: 3 }
const _hoisted_23 = {
  key: 1,
  class: "client-view"
}
const _hoisted_24 = {
  key: 0,
  class: "bsp-onboarding-section",
  "aria-labelledby": "connect-whatsapp-title"
}
const _hoisted_25 = { class: "bsp-action-grid" }
const _hoisted_26 = {
  class: "bsp-channel-icon bsp-channel-icon--primary",
  "aria-hidden": "true"
}
const _hoisted_27 = {
  class: "bsp-channel-icon bsp-channel-icon--whatsapp",
  "aria-hidden": "true"
}
const _hoisted_28 = {
  key: 1,
  class: "bsp-section",
  "aria-live": "polite"
}
const _hoisted_29 = { class: "bsp-inline-loader" }
const _hoisted_30 = {
  key: 2,
  class: "no-client-view bsp-empty-page"
}

import { computed, onMounted, reactive, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { POSITION, useToast } from 'vue-toastification'
import {
  AButton,
  AEmptyViewCard,
  AIcon,
  AInput,
  ASelector,
  ATag
} from '@adereso-ai/vue-components'
import AuthService from '@/services/AuthService/AuthService'
import type { User } from '@/services/AuthService/types'
import type { Establishment } from '@/services/EstablishmentService/types'
import type { PhoneNumber } from '@/services/AccountService/types'
import AccountService from '@/services/AccountService/AccountService'
import EstablishmentService from '@/services/EstablishmentService/EstablishmentService'
import MetaService from '@/services/MetaService/MetaService'
import BspPageHeader from '@/components/BspPageHeader.vue'
import SpinnerLoader from '@/components/loaders/SpinnerLoader.vue'
import SignupProgressModal from '@/components/SignupProgressModal.vue'
import { useEmbeddedSignup } from '@/composables/useEmbeddedSignup'
import {
  aggregateMetrics as aggregateMetricsByPhone,
  buildAnalyticsParams,
  computeTotalAverageDuration,
  downloadAnalyticsReport,
  fetchAnalytics,
  filterMetrics,
  formatDuration,
  getReportFilename as resolveReportFilename,
  type AnalyticsType
} from '@/utils/metricsAnalytics'


export default /*@__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const toast = useToast()

const loading = ref(true)
const loadingMetrics = ref(false)
const currentUser = ref<User>()
const currentEstablishment = ref<Establishment>()
const establishmentName = ref('')
const phoneNumbers = ref<PhoneNumber[]>([])
const metrics = ref<any[]>([])
const filteredMetrics = ref<any[]>([])
const aggregatedMetrics = ref<any[]>([])

const analyticsOptions = [
  { title: 'Per message', value: 'pricing' },
  { title: 'Conversations', value: 'conversation' },
  { title: 'Calls', value: 'call' }
]

const filters = reactive({
  analyticsType: 'pricing' as AnalyticsType,
  startDate: '',
  endDate: '',
  phoneNumbers: [] as { value: string, label: string }[]
})

const homeBreadcrumbs = [
  {
    title: 'Home',
    disabled: true,
    icon: 'tabler:home'
  }
]

const isStaff = computed(() => currentUser.value?.roles.includes('adereso_staff') === true)

const phoneNumberOptions = computed(() => phoneNumbers.value.map((phone) => ({
  value: phone.clean_display_phone_number,
  label: phone.display_phone_number
})))

const {
  signupFlowStatus,
  signupErrorMessage,
  signupSteps,
  isSignupActionDisabled,
  showSignupProgressModal,
  signupModalTitle,
  signupModalDescription,
  launchWhatsAppSignup,
  closeSignupModal
} = useEmbeddedSignup({
  async onSuccess () {
    await getEstablishment()
    if (currentEstablishment.value?.has_whatsapp_credentials) {
      await router.push({ name: 'whatsapp-business-accounts' })
    }
  }
})

const totalConversations = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.conversation ?? 0),
  0
))

const totalVolume = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.volume ?? 0),
  0
))

const totalCalls = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + (metric.count ?? 0),
  0
))

const totalAverageDuration = computed(() => computeTotalAverageDuration(aggregatedMetrics.value))

const totalCost = computed(() => aggregatedMetrics.value.reduce(
  (sum, metric) => sum + metric.cost,
  0
))

function applyFilters () {
  filteredMetrics.value = filterMetrics(metrics.value, filters)
  aggregatedMetrics.value = aggregateMetricsByPhone(filteredMetrics.value, filters.analyticsType)
}

function formatCost (value: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD'
  }).format(value)
}

function formatNumber (value: number) {
  return new Intl.NumberFormat('en-US').format(value)
}

async function getPhoneNumbers () {
  try {
    const accountService = await AccountService.getInstance()
    phoneNumbers.value = await accountService.getAllPhoneNumbers()
    filters.phoneNumbers = phoneNumberOptions.value
  } catch {
    toastError('Error getting WhatsApp Business phone numbers')
  }
}

async function fetchMetrics () {
  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters)
    const response = await fetchAnalytics(filters.analyticsType, metaService, 'all', params)
    metrics.value = response.data_points
    applyFilters()
  } catch (error) {
    toastError(`Error fetching metrics: ${String(error)}`)
  } finally {
    loadingMetrics.value = false
  }
}

async function downloadReport () {
  loadingMetrics.value = true
  try {
    const metaService = await MetaService.getInstance()
    const params = buildAnalyticsParams(filters, true)
    const response = await downloadAnalyticsReport(filters.analyticsType, metaService, 'all', params)
    const blob = new Blob([response], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    })
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.setAttribute('download', resolveReportFilename(filters.analyticsType))
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  } catch (error) {
    toastError(`Error downloading report: ${String(error)}`)
  } finally {
    loadingMetrics.value = false
  }
}

async function getEstablishment () {
  const establishmentService = await EstablishmentService.getInstance()
  try {
    currentEstablishment.value = await establishmentService.getCurrentEstablishment()
  } catch {
    currentEstablishment.value = undefined
  }
}

async function getCurrentUser () {
  const authService = await AuthService.getInstance()
  currentUser.value = authService.currentUser.value ?? undefined
  if (!currentUser.value) {
    await router.push({ name: 'login', query: route.query })
  }
}

onMounted(async () => {
  await router.isReady()
  await getCurrentUser()

  if (!currentUser.value) {
    loading.value = false
    return
  }

  await getEstablishment()

  if (currentEstablishment.value?.has_whatsapp_credentials) {
    await router.push({ name: 'whatsapp-business-accounts' })
  }

  if (isStaff.value) {
    await getPhoneNumbers()
    await fetchMetrics()
  }

  loading.value = false
})

async function createEstablishment () {
  if (!establishmentName.value.trim()) {
    toastError('Establishment name is required')
    return
  }
  const establishmentService = await EstablishmentService.getInstance()
  await establishmentService.createEstablishment(establishmentName.value.trim())
  await router.push('/')
}

function toastError (errorMessage: string) {
  toast.error(errorMessage, {
    position: POSITION.BOTTOM_LEFT
  })
}

return (_ctx: any,_cache: any) => {
  const _component_v_card = _resolveComponent("v-card")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (!loading.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          (isStaff.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                _createVNode(BspPageHeader, {
                  breadcrumbs: homeBreadcrumbs,
                  title: "Operations analytics",
                  description: "Review WhatsApp usage and cost across all connected accounts.",
                  eyebrow: "Adereso staff",
                  icon: "tabler:chart-bar"
                }),
                _createElementVNode("section", _hoisted_4, [
                  _cache[17] || (_cache[17] = _createElementVNode("div", { class: "bsp-section-heading" }, [
                    _createElementVNode("div", null, [
                      _createElementVNode("span", { class: "bsp-section-heading__eyebrow" }, "Analytics"),
                      _createElementVNode("h2", { id: "metrics-title" }, "Usage metrics"),
                      _createElementVNode("p", null, "Filter by metric, date range, and phone number before downloading a report.")
                    ])
                  ], -1)),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(_unref(ASelector), {
                      modelValue: filters.analyticsType,
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((filters.analyticsType) = $event)),
                      items: analyticsOptions,
                      "item-title": "title",
                      "item-value": "value",
                      label: "Analytics type"
                    }, null, 8, ["modelValue"]),
                    _createElementVNode("div", _hoisted_6, [
                      _cache[7] || (_cache[7] = _createElementVNode("label", { for: "home-start-date" }, "From", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "home-start-date",
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((filters.startDate) = $event)),
                        type: "date"
                      }, null, 512), [
                        [_vModelText, filters.startDate]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_7, [
                      _cache[8] || (_cache[8] = _createElementVNode("label", { for: "home-end-date" }, "To", -1)),
                      _withDirectives(_createElementVNode("input", {
                        id: "home-end-date",
                        "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((filters.endDate) = $event)),
                        type: "date"
                      }, null, 512), [
                        [_vModelText, filters.endDate]
                      ])
                    ]),
                    _createVNode(_unref(ASelector), {
                      modelValue: filters.phoneNumbers,
                      "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((filters.phoneNumbers) = $event)),
                      items: phoneNumberOptions.value,
                      "item-title": "label",
                      "item-value": "value",
                      label: "Phone numbers",
                      placeholder: "Select phone numbers",
                      multiple: "",
                      "return-object": "",
                      chips: "",
                      "closable-chips": "",
                      "multiple-max-selection-show": 2
                    }, null, 8, ["modelValue", "items"])
                  ]),
                  _createElementVNode("div", _hoisted_8, [
                    _createVNode(_unref(AButton), {
                      color: "blue",
                      "prepend-icon": "tabler:filter",
                      loading: loadingMetrics.value,
                      onClick: fetchMetrics
                    }, {
                      default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                        _createTextVNode(" Apply filters ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading"]),
                    _createVNode(_unref(AButton), {
                      variant: "outlined",
                      "prepend-icon": "tabler:download",
                      loading: loadingMetrics.value,
                      onClick: downloadReport
                    }, {
                      default: _withCtx(() => [...(_cache[10] || (_cache[10] = [
                        _createTextVNode(" Download report ", -1)
                      ]))]),
                      _: 1
                    }, 8, ["loading"])
                  ]),
                  (loadingMetrics.value)
                    ? (_openBlock(), _createElementBlock("div", _hoisted_9, [
                        _createVNode(SpinnerLoader),
                        _cache[11] || (_cache[11] = _createElementVNode("span", null, "Loading metrics…", -1))
                      ]))
                    : (aggregatedMetrics.value.length)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createElementVNode("table", null, [
                            _createElementVNode("thead", null, [
                              _createElementVNode("tr", null, [
                                _cache[12] || (_cache[12] = _createElementVNode("th", null, "Phone number", -1)),
                                _cache[13] || (_cache[13] = _createElementVNode("th", null, "WABA", -1)),
                                (filters.analyticsType === 'conversation')
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_11, "Conversations"))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'pricing')
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_12, "Messages"))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'call')
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_13, "Calls"))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'call')
                                  ? (_openBlock(), _createElementBlock("th", _hoisted_14, "Average duration"))
                                  : _createCommentVNode("", true),
                                _cache[14] || (_cache[14] = _createElementVNode("th", null, "Cost", -1))
                              ])
                            ]),
                            _createElementVNode("tbody", null, [
                              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(aggregatedMetrics.value, (metric, index) => {
                                return (_openBlock(), _createElementBlock("tr", { key: index }, [
                                  _createElementVNode("td", null, _toDisplayString(metric.phone_number), 1),
                                  _createElementVNode("td", null, _toDisplayString(metric.account_name), 1),
                                  (filters.analyticsType === 'conversation')
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_15, _toDisplayString(formatNumber(metric.conversation)), 1))
                                    : _createCommentVNode("", true),
                                  (filters.analyticsType === 'pricing')
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_16, _toDisplayString(formatNumber(metric.volume)), 1))
                                    : _createCommentVNode("", true),
                                  (filters.analyticsType === 'call')
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_17, _toDisplayString(formatNumber(metric.count)), 1))
                                    : _createCommentVNode("", true),
                                  (filters.analyticsType === 'call')
                                    ? (_openBlock(), _createElementBlock("td", _hoisted_18, _toDisplayString(_unref(formatDuration)(metric.average_duration)), 1))
                                    : _createCommentVNode("", true),
                                  _createElementVNode("td", null, _toDisplayString(formatCost(metric.cost)), 1)
                                ]))
                              }), 128))
                            ]),
                            _createElementVNode("tfoot", null, [
                              _createElementVNode("tr", null, [
                                _cache[15] || (_cache[15] = _createElementVNode("td", null, null, -1)),
                                _cache[16] || (_cache[16] = _createElementVNode("td", null, [
                                  _createElementVNode("strong", null, "Total")
                                ], -1)),
                                (filters.analyticsType === 'conversation')
                                  ? (_openBlock(), _createElementBlock("td", _hoisted_19, [
                                      _createElementVNode("strong", null, _toDisplayString(formatNumber(totalConversations.value)), 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'pricing')
                                  ? (_openBlock(), _createElementBlock("td", _hoisted_20, [
                                      _createElementVNode("strong", null, _toDisplayString(formatNumber(totalVolume.value)), 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'call')
                                  ? (_openBlock(), _createElementBlock("td", _hoisted_21, [
                                      _createElementVNode("strong", null, _toDisplayString(formatNumber(totalCalls.value)), 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                (filters.analyticsType === 'call')
                                  ? (_openBlock(), _createElementBlock("td", _hoisted_22, [
                                      _createElementVNode("strong", null, _toDisplayString(_unref(formatDuration)(totalAverageDuration.value)), 1)
                                    ]))
                                  : _createCommentVNode("", true),
                                _createElementVNode("td", null, [
                                  _createElementVNode("strong", null, _toDisplayString(formatCost(totalCost.value)), 1)
                                ])
                              ])
                            ])
                          ])
                        ]))
                      : (_openBlock(), _createBlock(_unref(AEmptyViewCard), {
                          key: 2,
                          icon: "tabler:chart-bar-off",
                          title: "No metrics to display",
                          description: "Adjust the filters and try again."
                        }))
                ])
              ]))
            : (currentUser.value && currentEstablishment.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                  _createVNode(BspPageHeader, {
                    breadcrumbs: homeBreadcrumbs,
                    title: currentEstablishment.value.has_whatsapp_credentials ? 'WhatsApp accounts' : 'Connect WhatsApp',
                    description: currentEstablishment.value.has_whatsapp_credentials
            ? `Opening the accounts connected to ${currentEstablishment.value.name}.`
            : `Choose how you want to connect ${currentEstablishment.value.name} to Adereso BSP.`,
                    eyebrow: "WhatsApp setup",
                    icon: "tabler:brand-whatsapp"
                  }, {
                    meta: _withCtx(() => [
                      _createVNode(_unref(ATag), {
                        text: currentEstablishment.value.name,
                        color: "brand",
                        type: "secondary",
                        "prepend-icon": "tabler:building"
                      }, null, 8, ["text"])
                    ]),
                    _: 1
                  }, 8, ["title", "description"]),
                  (!currentEstablishment.value.has_whatsapp_credentials)
                    ? (_openBlock(), _createElementBlock("section", _hoisted_24, [
                        _cache[22] || (_cache[22] = _createElementVNode("div", { class: "bsp-section-heading" }, [
                          _createElementVNode("div", null, [
                            _createElementVNode("span", { class: "bsp-section-heading__eyebrow" }, "Set up a channel"),
                            _createElementVNode("h2", { id: "connect-whatsapp-title" }, "Add a phone number or WABA"),
                            _createElementVNode("p", null, "Choose the flow that matches the current status of your phone number.")
                          ])
                        ], -1)),
                        _createElementVNode("div", _hoisted_25, [
                          _createVNode(_component_v_card, {
                            class: "bsp-action-card",
                            variant: "flat"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_26, [
                                _createVNode(_unref(AIcon), {
                                  icon: "tabler:device-mobile-plus",
                                  color: "primary",
                                  size: "22"
                                })
                              ]),
                              _cache[19] || (_cache[19] = _createElementVNode("div", { class: "bsp-action-card__copy" }, [
                                _createElementVNode("h3", null, "New phone number or migration"),
                                _createElementVNode("p", null, "Create or select a WABA, register a new number, or migrate one from another BSP.")
                              ], -1)),
                              _createVNode(_unref(AButton), {
                                color: "blue",
                                "prepend-icon": "tabler:plus",
                                disabled: _unref(isSignupActionDisabled),
                                onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(launchWhatsAppSignup)('cloud_api')))
                              }, {
                                default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                                  _createTextVNode(" Register phone number ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["disabled"])
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_v_card, {
                            class: "bsp-action-card",
                            variant: "flat"
                          }, {
                            default: _withCtx(() => [
                              _createElementVNode("span", _hoisted_27, [
                                _createVNode(_unref(AIcon), {
                                  icon: "tabler:brand-whatsapp",
                                  color: "success",
                                  size: "22"
                                })
                              ]),
                              _cache[21] || (_cache[21] = _createElementVNode("div", { class: "bsp-action-card__copy" }, [
                                _createElementVNode("h3", null, "WhatsApp Business App"),
                                _createElementVNode("p", null, "Connect an active app number and enable Coexistence with Cloud API.")
                              ], -1)),
                              _createVNode(_unref(AButton), {
                                variant: "outlined",
                                color: "blue",
                                "prepend-icon": "tabler:link",
                                disabled: _unref(isSignupActionDisabled),
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(launchWhatsAppSignup)('coexistence')))
                              }, {
                                default: _withCtx(() => [...(_cache[20] || (_cache[20] = [
                                  _createTextVNode(" Connect app ", -1)
                                ]))]),
                                _: 1
                              }, 8, ["disabled"])
                            ]),
                            _: 1
                          })
                        ])
                      ]))
                    : (_openBlock(), _createElementBlock("section", _hoisted_28, [
                        _createElementVNode("div", _hoisted_29, [
                          _createVNode(SpinnerLoader),
                          _cache[23] || (_cache[23] = _createElementVNode("span", null, "Opening your WhatsApp Business Accounts…", -1))
                        ])
                      ]))
                ]))
              : (_openBlock(), _createElementBlock("div", _hoisted_30, [
                  _createVNode(BspPageHeader, {
                    breadcrumbs: homeBreadcrumbs,
                    title: "Set up your establishment",
                    description: "Create an organization to start managing WhatsApp Business.",
                    eyebrow: "First step",
                    icon: "tabler:building-plus"
                  }),
                  _createVNode(_component_v_card, {
                    class: "bsp-form-card",
                    variant: "flat"
                  }, {
                    default: _withCtx(() => [
                      _cache[25] || (_cache[25] = _createElementVNode("h2", null, "New establishment", -1)),
                      _cache[26] || (_cache[26] = _createElementVNode("p", null, "We'll use this name to identify your operation within the platform.", -1)),
                      _createVNode(_unref(AInput), {
                        modelValue: establishmentName.value,
                        "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((establishmentName).value = $event)),
                        label: "Establishment name",
                        placeholder: "E.g. Adereso Chile"
                      }, null, 8, ["modelValue"]),
                      _createVNode(_unref(AButton), {
                        color: "blue",
                        block: "",
                        "prepend-icon": "tabler:plus",
                        onClick: createEstablishment
                      }, {
                        default: _withCtx(() => [...(_cache[24] || (_cache[24] = [
                          _createTextVNode(" Create establishment ", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]))
        ]))
      : (_openBlock(), _createBlock(SpinnerLoader, { key: 1 })),
    _createVNode(SignupProgressModal, {
      visible: _unref(showSignupProgressModal),
      title: _unref(signupModalTitle),
      description: _unref(signupModalDescription),
      steps: _unref(signupSteps),
      "error-message": _unref(signupErrorMessage),
      "flow-status": _unref(signupFlowStatus),
      onClose: _unref(closeSignupModal)
    }, null, 8, ["visible", "title", "description", "steps", "error-message", "flow-status", "onClose"])
  ]))
}
}

})